import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const AuthCtx = createContext(null);

/**
 * AuthProvider — thin business-logic layer + JWT silent-refresh.
 *
 * Feb 2026 (P0 auth session fix): il backend rilascia `refresh_token` come
 * cookie httpOnly (samesite=none, secure). Su 401 axios chiama
 * POST /api/auth/refresh con `withCredentials:true`, il browser invia
 * automaticamente il cookie, si ottiene un nuovo access_token e la request
 * originale viene ritentata UNA SOLA VOLTA. Se il refresh fallisce → logout.
 *
 * v43.35.13 — Trusted device / persistent login:
 *  - `login(email, password, rememberMe)` invia `remember_me` al backend che
 *    imposta il refresh cookie con TTL lungo (30 giorni) se true.
 *  - Bootstrap: se manca access_token in localStorage OPPURE `fetchMe` fallisce
 *    con 401, si tenta UN silent refresh via cookie httpOnly. Solo se anche
 *    il refresh fallisce si mostra la /login. Il cookie httpOnly stesso è
 *    la credenziale di sessione — nessun cookie leggibile lato JS controlla
 *    la scelta (server-side authoritative).
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => localStorage.getItem("access_token"));
  const tokenRef = useRef(token);
  useEffect(() => { tokenRef.current = token; }, [token]);

  const authHeaders = useCallback(() => (
    tokenRef.current ? { Authorization: `Bearer ${tokenRef.current}` } : {}
  ), []);

  // fetchMe restituisce:
  //   { ok: true, me }         → sessione valida
  //   { ok: false, status: N } → 401 o altro; il bootstrap decide se refresh
  const fetchMe = useCallback(async (tok) => {
    const useTok = tok ?? tokenRef.current;
    if (!useTok) return { ok: false, status: 401 };
    try {
      const { data } = await axios.get(`${API}/api/auth/me`, {
        headers: { Authorization: `Bearer ${useTok}` },
      });
      setUser(data);
      return { ok: true, me: data };
    } catch (e) {
      return { ok: false, status: e?.response?.status || 0 };
    }
  }, []);

  // v43.35.13 — Bootstrap silent-refresh strategy.
  // 1) se c'è access_token in localStorage → tenta fetchMe
  //    - success → sessione ripristinata
  //    - 401     → tenta doRefresh + retry fetchMe
  // 2) se NON c'è access_token → tenta doRefresh diretto (il cookie httpOnly
  //    refresh_token può ancora essere valido)
  //    - success → fetchMe con il nuovo access_token
  //    - fail    → user=null (login richiesto)
  // Nessun loop: doRefresh usa refreshPromiseRef come singleton, e ogni
  // fallimento di refresh porta a un solo esito terminale (user=null).
  useEffect(() => {
    (async () => {
      const initial = tokenRef.current;
      let r = await fetchMe(initial);
      if (!r.ok) {
        // access_token assente/scaduto/invalido → prova silent refresh
        try {
          const newTok = await doRefresh();
          if (newTok) {
            r = await fetchMe(newTok);
          }
        } catch {
          // refresh cookie assente o revocato → login richiesto
        }
        if (!r.ok) {
          // pulisci uno stato locale eventualmente stantio
          localStorage.removeItem("access_token");
          tokenRef.current = null;
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password, rememberMe = false) => {
    // withCredentials=true → il browser accetta il cookie httpOnly refresh_token
    // che il backend imposta nella response di /api/auth/login.
    const { data } = await axios.post(`${API}/api/auth/login`,
      { email, password, remember_me: !!rememberMe },
      { withCredentials: true });
    localStorage.setItem("access_token", data.access_token);
    tokenRef.current = data.access_token;
    setToken(data.access_token);
    const r = await fetchMe(data.access_token);
    return r.ok ? r.me : null;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/api/auth/logout`, {},
        { headers: authHeaders(), withCredentials: true });
    } catch {}
    localStorage.removeItem("access_token");
    tokenRef.current = null;
    setToken(null);
    setUser(null);
  };

  // ---- Silent refresh (concurrent-safe, retry-once) ----
  const refreshPromiseRef = useRef(null);
  const logoutStartedRef = useRef(false);
  const doRefresh = useCallback(async () => {
    if (!refreshPromiseRef.current) {
      refreshPromiseRef.current = axios.post(`${API}/api/auth/refresh`, {},
        { withCredentials: true })
        .then((r) => {
          const newTok = r?.data?.access_token;
          if (!newTok) throw new Error("no_access_token_in_refresh");
          localStorage.setItem("access_token", newTok);
          tokenRef.current = newTok;
          setToken(newTok);
          return newTok;
        })
        .finally(() => { refreshPromiseRef.current = null; });
    }
    return refreshPromiseRef.current;
  }, []);

  const _hardLogout = useCallback(() => {
    if (logoutStartedRef.current) return;
    logoutStartedRef.current = true;
    localStorage.removeItem("access_token");
    tokenRef.current = null;
    setToken(null);
    setUser(null);
  }, []);

  // Centralized request wrapper — every apiGet/apiPost/apiPatch/apiDelete
  // goes through this. On 401 (except on /auth/refresh itself), attempt one
  // silent refresh and retry the original request exactly once.
  const _request = useCallback(async (method, path, body) => {
    const doCall = (tok) => {
      const cfg = tok ? { headers: { Authorization: `Bearer ${tok}` } } : {};
      const url = `${API}${path}`;
      if (method === "get") return axios.get(url, cfg);
      if (method === "delete") return axios.delete(url, cfg);
      if (method === "post") return axios.post(url, body, cfg);
      if (method === "patch") return axios.patch(url, body, cfg);
      throw new Error("unsupported_method");
    };
    try {
      const r = await doCall(tokenRef.current);
      return r.data;
    } catch (e) {
      const status = e?.response?.status;
      // Only 401 on non-refresh endpoints trigger the silent-refresh flow.
      const isRefreshCall = path.endsWith("/api/auth/refresh") || path.endsWith("/auth/refresh");
      if (status !== 401 || isRefreshCall) {
        const err = new Error(e?.response?.data?.detail?.code || e?.response?.data?.detail || e.message);
        err.data = e?.response?.data; err.status = status;
        throw err;
      }
      // Try refresh once
      try {
        const newTok = await doRefresh();
        const r2 = await doCall(newTok);   // retry-once — no further retries
        return r2.data;
      } catch (e2) {
        // Refresh failed OR retry still 401 → clean logout
        _hardLogout();
        const err = new Error("session_expired");
        err.status = 401;
        throw err;
      }
    }
  }, [doRefresh, _hardLogout]);

  const apiGet    = useCallback((p)    => _request("get", p), [_request]);
  const apiPost   = useCallback((p, b) => _request("post", p, b), [_request]);
  const apiDelete = useCallback((p)    => _request("delete", p), [_request]);
  const apiPatch  = useCallback((p, b) => _request("patch", p, b), [_request]);

  return (
    <AuthCtx.Provider value={{ user, loading, token, login, logout, apiGet, apiPost, apiDelete, apiPatch }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
